/**
 * Styled XLSX export for Cari Hareketler — built with exceljs (lazy-loaded)
 * so the workbook carries real design: letterhead block, parameter strip,
 * navy header band, zebra-free but bordered rows, real numeric cells with
 * '#,##0.00' formats, an amber tag on manual rows, per-currency turnover
 * totals and a signed BAKİYE line. Shares the MovementDoc contract (and so the
 * exact same data) with the PDF export.
 *
 * Two modes, like the PDF: 'list' (global log, Cari column) and 'statement'
 * (one cari's ekstre, running TL balance column).
 */
import {
  type MovementDoc,
  type MovementDocRow,
  fmtDocDate,
  rowBalance,
  sortCurrencyTotals,
} from './cari-movements-doc';

const NAVY = 'FF1E3A5F';
const NAVY_SOFT = 'FFEEF2F7';
const INK = 'FF111827';
const INK_SOFT = 'FF4B5563';
const WHITE = 'FFFFFFFF';
const GREEN = 'FF047857';
const RED = 'FFB91C1C';
const AMBER = 'FFB45309';
const LINE = 'FFE5E7EB';
const ZEBRA = 'FFFAFBFC';

const NUM_FMT = '#,##0.00;[Red]-#,##0.00';

interface ColDef {
  key: keyof MovementDocRow | 'no';
  header: string;
  width: number;
  num?: boolean; // numeric cell (right aligned, NUM_FMT)
  turnover?: boolean; // one of the 4 turnover columns used in totals
}

function colLetter(n: number): string {
  let str = '';
  while (n > 0) {
    const m = (n - 1) % 26;
    str = String.fromCharCode(65 + m) + str;
    n = Math.floor((n - 1) / 26);
  }
  return str;
}

function buildColumns(doc: MovementDoc): ColDef[] {
  const c = doc.meta.columns;
  if (doc.meta.mode === 'statement') {
    return [
      { key: 'no', header: c.no, width: 5 },
      { key: 'date', header: c.date, width: 12 },
      { key: 'voucherNo', header: c.voucher, width: 13 },
      { key: 'description', header: c.description, width: 42 },
      { key: 'currency', header: c.currency, width: 7 },
      { key: 'fxDebit', header: c.fxDebit, width: 15, num: true, turnover: true },
      { key: 'fxCredit', header: c.fxCredit, width: 15, num: true, turnover: true },
      { key: 'tryDebit', header: c.tryDebit, width: 15, num: true, turnover: true },
      { key: 'tryCredit', header: c.tryCredit, width: 15, num: true, turnover: true },
      { key: 'tryBalance', header: c.balance, width: 16, num: true },
    ];
  }
  return [
    { key: 'no', header: c.no, width: 5 },
    { key: 'date', header: c.date, width: 12 },
    { key: 'counterpartyName', header: c.cari, width: 34 },
    { key: 'cariType', header: c.type, width: 6 },
    { key: 'cariCode', header: c.code, width: 10 },
    { key: 'voucherNo', header: c.voucher, width: 13 },
    { key: 'description', header: c.description, width: 30 },
    { key: 'currency', header: c.currency, width: 7 },
    { key: 'fxDebit', header: c.fxDebit, width: 15, num: true, turnover: true },
    { key: 'fxCredit', header: c.fxCredit, width: 15, num: true, turnover: true },
    { key: 'tryDebit', header: c.tryDebit, width: 15, num: true, turnover: true },
    { key: 'tryCredit', header: c.tryCredit, width: 15, num: true, turnover: true },
  ];
}

/** Build the styled workbook (shared by the browser export and Node tests). */
export async function buildCariMovementsWorkbook(doc: MovementDoc) {
  const mod = (await import('exceljs')) as typeof import('exceljs') & { default?: typeof import('exceljs') };
  const ExcelJS = mod.default ?? mod;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = doc.company.name;
  workbook.created = new Date();

  const { meta, data, company } = doc;
  const columns = buildColumns(doc);
  const lastCol = colLetter(columns.length);
  const firstTurnover = columns.findIndex((c) => c.turnover) + 1; // 1-based

  const ws = workbook.addWorksheet(meta.title.slice(0, 28) || 'Cari Hareketler', {
    pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 } },
    // The header-row freeze is applied below, once we know the header's row number.
  });
  ws.columns = columns.map((c) => ({ width: c.width }));

  const thin = { style: 'thin' as const, color: { argb: LINE } };

  // ── Letterhead ─────────────────────────────────────────────────────────
  ws.mergeCells(`A1:${lastCol}1`);
  const titleCell = ws.getCell('A1');
  titleCell.value = company.name;
  titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: NAVY } };
  ws.getRow(1).height = 22;

  ws.mergeCells(`A2:${lastCol}2`);
  ws.getCell('A2').value = `${meta.title} · ${meta.periodLabel}`;
  ws.getCell('A2').font = { name: 'Calibri', size: 11, bold: true, color: { argb: INK } };

  ws.mergeCells(`A3:${lastCol}3`);
  ws.getCell('A3').value = meta.params.map((p) => `${p.label}: ${p.value}`).join('   ·   ');
  ws.getCell('A3').font = { name: 'Calibri', size: 9, color: { argb: INK_SOFT } };

  ws.mergeCells(`A4:${lastCol}4`);
  ws.getCell('A4').value = `${company.address} · ${company.phone} · ${company.email}    |    ${meta.generatedLabel}`;
  ws.getCell('A4').font = { name: 'Calibri', size: 8, italic: true, color: { argb: INK_SOFT } };

  // Statement counterparty band
  if (meta.mode === 'statement' && data.cari) {
    const bandRow = ws.addRow([]);
    ws.mergeCells(`A${bandRow.number}:${lastCol}${bandRow.number}`);
    const cell = ws.getCell(`A${bandRow.number}`);
    cell.value = `${data.cari.name}   ·   ${data.cari.code}${data.cari.type ? `  ·  ${data.cari.type}` : ''}`;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: WHITE } };
    bandRow.height = 20;
    cell.alignment = { vertical: 'middle' };
  } else {
    ws.addRow([]);
  }

  // ── Header band ──────────────────────────────────────────────────────────
  const header = ws.addRow(columns.map((c) => c.header));
  header.height = 18;
  header.eachCell((cell, col) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: WHITE } };
    cell.alignment = { vertical: 'middle', horizontal: columns[col - 1]?.num ? 'right' : 'left' };
  });
  // Freeze everything above & including the header row.
  ws.views = [{ state: 'frozen', ySplit: header.number }];

  // ── Data rows ────────────────────────────────────────────────────────────
  if (data.rows.length === 0) {
    const emptyRow = ws.addRow([]);
    ws.mergeCells(`A${emptyRow.number}:${lastCol}${emptyRow.number}`);
    ws.getCell(`A${emptyRow.number}`).value = meta.emptyLabel;
    ws.getCell(`A${emptyRow.number}`).font = { name: 'Calibri', size: 10, italic: true, color: { argb: INK_SOFT } };
    ws.getCell(`A${emptyRow.number}`).alignment = { horizontal: 'center' };
  }

  data.rows.forEach((r: MovementDocRow, i) => {
    const values = columns.map((c) => {
      if (c.key === 'no') return i + 1;
      if (c.key === 'date') return fmtDocDate(r.date);
      // Raw voucher code (manual rows are flagged by the amber font below, so the
      // cell value stays exact-match filterable in Excel).
      if (c.key === 'voucherNo') return r.voucherNo;
      // Statement running balance honours the per-app native/TL choice.
      if (c.key === 'tryBalance') return rowBalance(r, meta.balanceNative);
      const v = r[c.key];
      if (c.num) {
        const n = Number(v ?? 0);
        return n === 0 ? null : n; // blank zero turnover cells, mirroring the PDF/screen
      }
      return v ?? '';
    });
    const row = ws.addRow(values);
    const zebra = i % 2 === 1;
    row.eachCell({ includeEmpty: true }, (cell, col) => {
      const def = columns[col - 1];
      cell.font = { name: 'Calibri', size: 9, color: { argb: def?.key === 'counterpartyName' ? INK : INK_SOFT } };
      if (zebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
      cell.border = { bottom: thin };
      if (def?.num) {
        cell.numFmt = NUM_FMT;
        cell.alignment = { horizontal: 'right' };
        cell.font = { name: 'Calibri', size: 9, color: { argb: INK } };
        if (def.key === 'tryBalance') {
          const bal = rowBalance(r, meta.balanceNative);
          cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: bal >= 0 ? GREEN : RED } };
        }
      }
    });
    if (r.isManual) {
      const vCol = columns.findIndex((c) => c.key === 'voucherNo') + 1;
      if (vCol > 0) row.getCell(vCol).font = { name: 'Calibri', size: 9, bold: true, color: { argb: AMBER } };
    }
  });

  // ── Per-currency totals ──────────────────────────────────────────────────
  if (data.rows.length > 0) {
    ws.addRow([]);
    for (const cur of sortCurrencyTotals(data.totalsByCurrency)) {
      const net = cur.currency === 'TRY' ? cur.tryNet : cur.fxNet;

      const tRow = ws.addRow([]);
      // Label spanning everything left of the turnover columns.
      ws.mergeCells(`A${tRow.number}:${colLetter(firstTurnover - 1)}${tRow.number}`);
      const labelCell = ws.getCell(`A${tRow.number}`);
      labelCell.value = `${meta.currencyNames[cur.currency] ?? cur.currency} ${meta.totalLabel}`;
      labelCell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: NAVY } };
      labelCell.alignment = { horizontal: 'right' };

      const turnoverVals = [cur.fxDebit, cur.fxCredit, cur.tryDebit, cur.tryCredit];
      turnoverVals.forEach((v, idx) => {
        const cell = tRow.getCell(firstTurnover + idx);
        cell.value = v;
        cell.numFmt = NUM_FMT;
        cell.font = { name: 'Calibri', size: 9.5, bold: true, color: { argb: INK } };
        cell.alignment = { horizontal: 'right' };
      });
      tRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY_SOFT } };
      });

      // Signed balance line.
      const bRow = ws.addRow([]);
      ws.mergeCells(`A${bRow.number}:${lastCol}${bRow.number}`);
      const bCell = ws.getCell(`A${bRow.number}`);
      bCell.value = `${meta.balanceLabel}: ${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(net))} ${cur.currency} ${net >= 0 ? meta.debitSide : meta.creditSide}`;
      bCell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: net >= 0 ? GREEN : RED } };
      bCell.alignment = { horizontal: 'right' };
      bCell.border = { bottom: { style: 'double', color: { argb: LINE } } };
    }
  }

  return workbook;
}

/** Browser entry point: build the workbook and trigger a download. */
export async function exportCariMovementsXlsx(doc: MovementDoc, filename: string): Promise<void> {
  const workbook = await buildCariMovementsWorkbook(doc);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
